'use client'

import { motion, useReducedMotion } from 'motion/react'
import type { ReactNode } from 'react'

export type RevealProps = {
  children: ReactNode
  className?: string
  /** Stagger delay in seconds. */
  delay?: number
}

/**
 * Subtle on-scroll entrance (fade + rise) used for below-the-fold landing
 * sections. Fires once. Renders children statically under reduced motion
 * so content is never gated behind animation for those users.
 *
 * Kept off the hero / first viewport so the static shell paints instantly
 * and LCP is never hidden behind an opacity transition.
 */
export function Reveal({ children, className, delay = 0 }: RevealProps) {
  const shouldReduceMotion = useReducedMotion()

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15, margin: '0px 0px -80px 0px' }}
      transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1], delay }}
    >
      {children}
    </motion.div>
  )
}
